import './i18n';
import './index.css';
import React, { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import TagManager from 'react-gtm-module';
import App from './app';
import reportWebVitals from './report-web-vitals';
import configureAppStore from './store/store';
import { Provider } from 'react-redux';
import { forkJoin, of } from 'rxjs';
import setupServiceContext from './services/services';
import { AuthService } from './services/auth.service';
import { catchError, tap } from 'rxjs/operators';
import { guard, systemConfiguration, user } from './store/global-slice';
import { InternalAuthService } from './services/internal-auth.service';
import { SystemConfigurationService } from './services/systemConfiguration.service';
import ReactModal from 'react-modal';
import { SystemConfiguration } from './enums/system-configuration';
import * as Sentry from '@sentry/react';

const APP_ROOT = 'root';
const USE_SIMPLE_GOOGLE_TAG_MANAGER = true;

enum Environment {
  PRODUCTION = 'FRONTEND_PRODUCTION',
  TEST = 'FRONTEND_TEST',
  DEFAULT = 'FRONTEND',
}

function sentryDsn() {
  switch (window.location.hostname) {
    case 'www.manslauks.lv':
      return 'https://c2e63a21a378e3c7b0042282b48fdfc6@o4506381519093760.ingest.sentry.io/4506421434449920';
    case 'test.manslauks.lv':
      return 'https://30e138e9ee8fc04a02bd55df276fe627@o4506381519093760.ingest.sentry.io/4506421434449920';
    default:
      return null;
  }
}

function environment() {
  switch (window.location.hostname) {
    case 'www.manslauks.lv':
      return Environment.PRODUCTION;
    case 'test.manslauks.lv':
      return Environment.TEST;
    default:
      return Environment.DEFAULT;
  }
}

Sentry.init({
  dsn: sentryDsn(),
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.1,
  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  replaysOnErrorSampleRate: 1.0,
  environment: environment(),

  integrations: [new Sentry.Replay()],
});

const store = configureAppStore();
setupServiceContext(store.dispatch);

ReactModal.setAppElement(`#${APP_ROOT}`);

/**
 * Check if user logged in, before trying to render app.
 */
forkJoin([
  new AuthService(store.dispatch, true).user().pipe(
    tap((userResponse) => {
      if (userResponse) {
        store.dispatch(guard('api'));
        store.dispatch(user(userResponse));
      }
    }),
    catchError(() => {
      return of(null);
    })
  ),
  new InternalAuthService(store.dispatch, true).user().pipe(
    tap((userResponse) => {
      if (userResponse) {
        store.dispatch(guard('ldap'));
        store.dispatch(user(userResponse));
      }
    }),
    catchError(() => {
      return of(null);
    })
  ),
  new SystemConfigurationService(store.dispatch, true).public().pipe(
    tap((configResponse) => {
      store.dispatch(systemConfiguration(configResponse));

      const { value: gtmId } = configResponse.find(
        (i) => i.key === SystemConfiguration.GTM_ID
      );
      const { value: auth } = configResponse.find(
        (i) => i.key === SystemConfiguration.GTM_AUTH
      );
      const { value: preview } = configResponse.find(
        (i) => i.key === SystemConfiguration.GTM_PREVIEW
      );

      TagManager.initialize({
        gtmId,
        auth: USE_SIMPLE_GOOGLE_TAG_MANAGER ? '' : auth,
        preview: USE_SIMPLE_GOOGLE_TAG_MANAGER ? '' : preview,
      });
    }),
    catchError(() => {
      return of(null);
    })
  ),
]).subscribe(() => {
  ReactDOM.createRoot(document.getElementById(APP_ROOT) as HTMLElement).render(
    <StrictMode>
      <Provider store={store}>
        <App />
      </Provider>
    </StrictMode>
  );
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
